import { useQuery } from "@tanstack/react-query";
import { workshopApi } from "../../../services/workshopApi";

/**
 * Custom hook to fetch and manage workshops data
 * 
 * Uses React Query for:
 * - Automatic caching (data persists when navigating away)
 * - Background refetching
 * - Stale-while-revalidate pattern
 *
 * @param {Object} params - Query parameters
 * @param {string} params.query - Search query
 * @param {string} params.category - Category filter
 * @param {string} params.field - Field filter
 * @param {string} params.format - Format filter (online/physical)
 * @param {string} params.pricing - Pricing filter (free/paid)
 * @param {string} params.status - Status filter
 * @param {number} params.minPrice - Minimum price
 * @param {number} params.maxPrice - Maximum price
 * @param {string} params.sortBy - Sort field
 * @param {string} params.sortOrder - Sort order (asc/desc)
 * @param {number} params.page - Page number
 * @param {number} params.limit - Items per page
 *
 * @returns {Object} { workshops, loading, error, pagination, refetch }
 */
function useWorkshops(params = {}) {
  // Build query key from all params (React Query uses this for caching)
  const queryKey = [
    "workshops",
    {
      query: params.query || "",
      category: params.category || "all",
      field: params.field || "all",
      format: params.format || "all",
      pricing: params.pricing || "all",
      status: params.status || "all",
      minPrice: params.minPrice ?? null,
      maxPrice: params.maxPrice ?? null,
      sortBy: params.sortBy || "createdAt",
      sortOrder: params.sortOrder || "desc",
      page: params.page || 1,
      limit: params.limit || 20,
    },
  ];

  /**
   * Fetch workshops from API
   */
  const fetchWorkshops = async () => {
    // Build query parameters, filtering out null/undefined/"all" values
    const queryParams = {};

    if (params.query) queryParams.query = params.query;
    if (params.category && params.category !== "all")
      queryParams.category = params.category;
    if (params.field && params.field !== "all")
      queryParams.field = params.field;
    if (params.format && params.format !== "all")
      queryParams.format = params.format;
    if (params.pricing && params.pricing !== "all")
      queryParams.pricing = params.pricing;
    if (params.status && params.status !== "all")
      queryParams.status = params.status;
    if (params.minPrice !== null && params.minPrice !== undefined) {
      queryParams.minPrice = params.minPrice;
    }
    if (params.maxPrice !== null && params.maxPrice !== undefined) {
      queryParams.maxPrice = params.maxPrice;
    }
    if (params.sortBy) queryParams.sortBy = params.sortBy;
    if (params.sortOrder) queryParams.sortOrder = params.sortOrder;

    queryParams.page = params.page || 1;
    queryParams.limit = params.limit || 20;

    const response = await workshopApi.getWorkshops(queryParams);

    if (response.success) {
      return {
        workshops: response.data.workshops || [],
        pagination: response.data.pagination || null,
      };
    } else {
      throw new Error(response.message || "Failed to fetch workshops");
    }
  };

  // Use React Query for caching and automatic refetching
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: fetchWorkshops,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes (formerly cacheTime)
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData, // Show old data while fetching new (keepPreviousData equivalent)
  });

  return {
    workshops: data?.workshops || [],
    loading: isLoading,
    isFetching, // True when refetching in background
    error,
    pagination: data?.pagination || null,
    refetch,
  };
}

export default useWorkshops;
